import React, { useState, useEffect, useCallback, useMemo } from "react";
import { BarChart3, TrendingUp, Users, CheckCircle, RefreshCw, Filter, Award } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from "../../customStyles/SelectDropdown";
import PageLoader from "../../helpers/PageLoader";

export default function KraOverview() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  // Dependency states for filters
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Selected Filter States
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");

  // Dynamic Financial Years list generation
  const availableYears = useMemo(() => {
    const years = [];
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    for (let y = currentYear + 1; y >= startYear; y--) {
      years.push({ label: `FY ${y}`, value: y });
    }
    return years;
  }, []);

  // Load org structural filter options on mount
  const loadFilterDependencies = useCallback(async () => {
    try {
      const [deptRes, divRes, desRes] = await Promise.all([
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url),
        api.get(SummaryApi.getAllDesignations?.url || "/api/hr/designations")
      ]);

      if (deptRes.data.success) setDepartments(deptRes.data.data.map(d => ({ label: d.name, value: d._id })));
      if (divRes.data.success) setDivisions(divRes.data.data.map(d => ({ label: d.name, value: d._id })));
      if (desRes.data.success) setDesignations(desRes.data.data.map(d => ({ label: d.title, value: d._id })));
    } catch {
      toast.error("Failed to load dashboard filters.");
    }
  }, []);

  // Fetch admin dashboard aggregated summary metrics
  const fetchDashboardSummary = useCallback(async () => {
    setLoading(true);
    try {
      // Dynamic query string building
      let endpoint = `${SummaryApi.getAdminKraOverview.url}?year=${selectedYear}`;
      if (selectedDepartment) endpoint += `&departmentId=${selectedDepartment}`;
      if (selectedDivision) endpoint += `&divisionId=${selectedDivision}`;
      if (selectedDesignation) endpoint += `&designationId=${selectedDesignation}`;

      const res = await api.get(endpoint);
      if (res.data.success) {
        setDashboardData(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch performance dashboard summary.");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedDepartment, selectedDivision, selectedDesignation]);

  useEffect(() => {
    loadFilterDependencies();
  }, [loadFilterDependencies]);

  useEffect(() => {
    fetchDashboardSummary();
  }, [fetchDashboardSummary]);

  // Dynamic calculated stats metrics
  const calculatedStats = useMemo(() => {
    if (!dashboardData) return { completionPercentage: "0", pendingActions: 0 };
    
    const { totalReviews, statusBreakdown } = dashboardData.reviews || {};
    const completedCount = statusBreakdown?.completed || 0;
    
    // Calculates percentage dynamically without crashing on zero division
    const completionPercentage = totalReviews > 0 
      ? ((completedCount / totalReviews) * 100).toFixed(0) 
      : "0";

    const pendingActions = (statusBreakdown?.underReview || 0) + (statusBreakdown?.disputed || 0);

    return {
      completionPercentage,
      pendingActions
    };
  }, [dashboardData]);

  const handleResetFilters = () => {
    setSelectedDepartment("");
    setSelectedDivision("");
    setSelectedDesignation("");
    setSelectedYear(new Date().getFullYear());
  };

  if (loading && !dashboardData) return <PageLoader />;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* FILTER CONTROL PANEL */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center gap-4 text-left">
          
          <div className="w-48">
            <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5">Review Year</span>
            <SelectDropdown 
              options={availableYears} 
              value={selectedYear} 
              onChange={setSelectedYear} 
              placeholder="All Years" 
              valueKey="value" 
              labelKey="label" 
              ChildClassName="h-9 text-xs border-slate-200" 
              searchable={true} 
            />
          </div>

          <div className="w-48">
            <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5">Division</span>
            <SelectDropdown 
              options={divisions} 
              value={selectedDivision} 
              onChange={setSelectedDivision} 
              placeholder="All Divisions" 
              valueKey="value" 
              labelKey="label" 
              ChildClassName="h-9 text-xs border-slate-200" 
              searchable={true} 
            />
          </div>

          <div className="w-48">
            <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5">Department</span>
            <SelectDropdown 
              options={departments} 
              value={selectedDepartment} 
              onChange={setSelectedDepartment} 
              placeholder="All Departments" 
              valueKey="value" 
              labelKey="label" 
              ChildClassName="h-9 text-xs border-slate-200" 
              searchable={true} 
            />
          </div>

          <div className="w-48">
            <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5">Designation</span>
            <SelectDropdown 
              options={designations} 
              value={selectedDesignation} 
              onChange={setSelectedDesignation} 
              placeholder="All Designations" 
              valueKey="value" 
              labelKey="label" 
              ChildClassName="h-9 text-xs border-slate-200" 
              searchable={true} 
            />
          </div>

          <div className="flex gap-2 mt-5">
            <button 
              type="button" 
              onClick={handleResetFilters} 
              className="flex items-center gap-1.5 px-4 py-2 border rounded-lg text-xs font-semibold h-9 border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <Filter className="h-4 w-4" /> Reset Filters
            </button>
            <button 
              type="button" 
              onClick={fetchDashboardSummary} 
              className="flex items-center justify-center p-2.5 border rounded-lg h-9 border-slate-200 hover:bg-slate-50 transition-transform active:scale-95"
            >
              <RefreshCw className="h-4 w-4 text-slate-500" />
            </button>
          </div>

        </div>
      </div>

      {/* METRIC STATS GRID CARD SECTION */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: Average Score */}
        <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm flex items-center gap-4 text-left">
          <div className="p-4 bg-red-50 rounded-2xl text-red-600 shrink-0">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none">Average Appraisal Score</p>
            <h3 className="text-xl font-extrabold text-gray-800 mt-1.5">
              {dashboardData?.reviews?.avgWeightedScoreOrganization?.toFixed(2) || "0.00"} / 100
            </h3>
            <span className="text-[9px] text-slate-400 font-bold">
              Avg Achievement: {dashboardData?.reviews?.avgAchievementOrganization?.toFixed(2) || "0.00"}%
            </span>
          </div>
        </div>

        {/* Card 2: Total Staff */}
        <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm flex items-center gap-4 text-left">
          <div className="p-4 bg-red-50 rounded-2xl text-red-600 shrink-0">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none">Total Evaluated Staff</p>
            <h3 className="text-xl font-extrabold text-gray-800 mt-1.5">
              {dashboardData?.assignments?.totalAssignments || 0} Staff
            </h3>
            <span className="text-[9px] text-emerald-600 font-bold">
              Acknowledged: {dashboardData?.assignments?.acknowledged || 0}
            </span>
          </div>
        </div>

        {/* Card 3: Completed Appraisals */}
        <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm flex items-center gap-4 text-left">
          <div className="p-4 bg-red-50 rounded-2xl text-red-600 shrink-0">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none">Completed Appraisals</p>
            <h3 className="text-xl font-extrabold text-gray-800 mt-1.5">
              {calculatedStats.completionPercentage}%
            </h3>
            <span className="text-[9px] text-slate-400 font-bold block truncate max-w-[160px]">
              {dashboardData?.reviews?.statusBreakdown?.completed || 0} / {dashboardData?.reviews?.totalReviews || 0} Closed
            </span>
          </div>
        </div>

        {/* Card 4: Pending Action Submissions */}
        <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm flex items-center gap-4 text-left">
          <div className="p-4 bg-red-50 rounded-2xl text-red-600 shrink-0">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none">Pending Action Reviews</p>
            <h3 className="text-xl font-extrabold text-gray-800 mt-1.5">
              {calculatedStats.pendingActions} Sheet(s)
            </h3>
            <span className="text-[9px] text-amber-600 font-bold">
              Under Review: {dashboardData?.reviews?.statusBreakdown?.underReview || 0}
            </span>
          </div>
        </div>
      </div>

      {/* DYNAMIC DEPARTMENT PERFORMANCE BOARD (Replaced the placeholder) */}
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-6">
        <div className="border-b pb-3 flex items-center gap-2 text-left">
          <span className="p-1.5 bg-red-50 rounded-md text-primary"><Award size={18} /></span>
          <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Department Performance Analysis</h3>
        </div>

        {dashboardData?.departmentPerformance?.length > 0 ? (
          <div className="space-y-4 max-h-[380px] overflow-y-auto pr-1">
            {dashboardData.departmentPerformance.map((dept, index) => (
              <div key={index} className="space-y-1.5 text-left">
                <div className="flex justify-between items-center text-xs font-bold">
                  <span className="text-gray-700 flex items-center gap-1.5">
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-gray-500 text-[9px]">{index + 1}</span>
                    {dept.departmentName}
                  </span>
                  <div className="space-x-2 shrink-0">
                    <span className="text-primary">{dept.averageScore}% Score</span>
                    <span className="text-gray-400 font-medium">({dept.reviewCount} reviews)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-primary h-full rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, dept.averageScore)}%` }} 
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
            <div className="p-3 bg-slate-50 border rounded-full text-slate-400">
              <BarChart3 className="w-6 h-6" />
            </div>
            <p className="text-xs text-gray-400 font-bold uppercase">No Department Performance Mapped</p>
            <p className="text-[11px] text-gray-400 max-w-xs">There are no completed appraisal sheets mapped for this cycle configuration yet.</p>
          </div>
        )}
      </div>

    </div>
  );
}